import type { Cart } from '@/js/types/cart.ts';
import { addCartItem, updateCartItemQuantity } from '@/js/api/cart.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import { ERROR_REQUEST_TEXT, getNotificationElement, showErrorNotify, showSuccessNotify } from '@/js/notification.ts';
import { formatPrice } from '@/js/utils.ts';
import { computed, ref, watch } from 'vue';

const DATA_CART_QUANTITY = 'data-counter';
const DATA_CART_TOTAL = 'data-cart-total';
const QUANTITY_SELECTOR = '.js-cart-quantity';
const TOTAL_SELECTOR = '.js-cart-total';
const totalElements = document.querySelectorAll<HTMLElement>(TOTAL_SELECTOR);

const quantityElements = document.querySelectorAll<HTMLElement>(QUANTITY_SELECTOR);

export function useCartTotal() {
  const cart = ref<Cart>();
  const products = computed((): Cart['items'] => cart.value?.items ?? []);
  const totals = computed((): Cart['totals'] => cart.value?.totals ?? []);

  const cartQuantity = computed(() => products.value.reduce((sum, item) => sum + item.quantity, 0));
  watch(cartQuantity, (newQty) => {
    for (const quantityElement of quantityElements) {
      quantityElement.setAttribute(DATA_CART_QUANTITY, newQty.toString());
    }
  });

  const cartTotalSum = computed(() => totals.value.find(item => item.type === 'sum')?.value ?? 0);
  watch(cartTotalSum, (newTotal) => {
    for (const totalElement of totalElements) {
      totalElement.setAttribute(DATA_CART_TOTAL, formatPrice(newTotal));
    }
  });

  async function updateCartItem(productId: number, quantity: number) {
    try {
      const { data } = await updateCartItemQuantity(productId, quantity);
      if (data) {
        cart.value = data;
      }
    }
    catch (e) {
      console.error(e);
    }
  }

  async function deleteCartItem(productId: number) {
    try {
      await updateCartItem(productId, 0);
    }
    catch (e) {
      console.log(e);
    }
  }

  async function addProductItem(productId: number, quantity: number) {
    try {
      const { data } = await addCartItem(productId, quantity);
      if (data) {
        cart.value = data;
        showSuccessNotify({ node: getNotificationElement(getLocaleValue('product_added_to'), getLocaleValue('to_cart'), '/cart') });
      }
    }
    catch (e) {
      showErrorNotify({ text: ERROR_REQUEST_TEXT });
      console.log(e);
    }
  }

  function setCartTotal(cartData: Cart) {
    cart.value = cartData;
  }

  return {
    updateCartItem,
    deleteCartItem,
    setCartTotal,
    products,
    totals,
    cartTotalSum,
    addProductItem,
  };
}
